import {
  SortableTask,
  TaskDropGroup,
  TaskOrder,
  type MoveToGroup,
  type CanMoveToGroup,
} from "./TaskOrder";
import { useServices } from "@hq/client-react";
import type { Priority, Project, Task } from "@hq/contracts";
import type { NowContext, TaskGroup } from "@hq/core";
import { Button, cn, EmptyState } from "@z/ui";
import { ListChecks } from "lucide-react";
import { type FocusEvent, type KeyboardEvent, type ReactNode, useRef, useState } from "react";
import { TaskRow } from "./TaskRow";

export interface TaskGroupListProps {
  /** Already filtered, sorted and grouped (by `core`). A single group with an empty label renders flat. */
  groups: readonly TaskGroup[];
  reorderable?: boolean;
  onMoveToGroup?: MoveToGroup;
  canMoveToGroup?: CanMoveToGroup;
  collapsedGroupKeys?: readonly string[];
  emptyGroupKeys?: readonly string[];
  collapsibleGroupKeys?: readonly string[];
  onToggleGroup?: (key: string) => void;
  now: NowContext;
  projects: readonly Project[];
  showProject?: boolean;
  density?: "compact" | "comfortable";
  onOpenTask?: (taskId: string) => void;
  /** Makes each row's tag badges clickable. */
  onTagClick?: (tag: string) => void;
  /** Rendered above the rows inside the keyboard scope, so `c` can focus its input. */
  quickAdd?: ReactNode;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  emptyIcon?: ReactNode;
  /** Group heading text override, e.g. to turn `2026-09-18` into "Fri 18 Sep". */
  formatGroupLabel?: (group: TaskGroup) => string;
  className?: string;
  "aria-label"?: string;
}

const PRIORITY_KEYS: Record<string, Priority | null> = {
  "1": "high",
  "2": "medium",
  "3": "low",
  "0": null,
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

/**
 * The keyboard-driven list body shared by every view. Roving focus lives in React state;
 * DOM focus follows it imperatively from the key handler. Single-key shortcuts fire only
 * when the event target is the list itself or one of its rows, never inside an input.
 *
 * Keys: ↑/↓ or k/j move · Enter open · x or Space complete · e rename · d deadline · t tags ·
 * 1/2/3/0 priority · Backspace/Delete delete · c quick add · Escape leave the list.
 */
export function TaskGroupList({
  groups: allGroups,
  onMoveToGroup,
  canMoveToGroup,
  collapsedGroupKeys = [],
  emptyGroupKeys = [],
  collapsibleGroupKeys = [],
  onToggleGroup,
  reorderable = false,
  now,
  projects,
  showProject = true,
  density = "comfortable",
  onOpenTask,
  onTagClick,
  quickAdd,
  emptyTitle = "Nothing here",
  emptyDescription,
  emptyAction,
  emptyIcon = <ListChecks />,
  formatGroupLabel,
  className,
  "aria-label": ariaLabel,
}: TaskGroupListProps) {
  const collapsedKeys = new Set(collapsedGroupKeys);
  const emptyKeys = new Set(emptyGroupKeys);
  const collapsibleKeys = new Set(collapsibleGroupKeys);
  const groups = allGroups.map((group) =>
    collapsedKeys.has(group.key) ? { ...group, tasks: [] } : group,
  );
  const services = useServices();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [active, setActive] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [dueOpenId, setDueOpenId] = useState<string | undefined>(undefined);
  const [tagsOpenId, setTagsOpenId] = useState<string | undefined>(undefined);

  const flat: Task[] = [];
  for (const g of groups) flat.push(...g.tasks);
  const taskIndices = new Map(flat.map((task, index) => [task.id, index]));
  // Rows come and go under the cursor (completing removes one); clamp instead of syncing.
  const focused = flat.length === 0 ? -1 : Math.min(focusedIndex, flat.length - 1);
  const focusedTask = focused >= 0 ? flat[focused] : undefined;

  const focusRow = (index: number) => {
    const next = Math.max(0, Math.min(index, flat.length - 1));
    setFocusedIndex(next);
    containerRef.current
      ?.querySelector<HTMLElement>(`[data-task-index="${next}"]`)
      ?.focus({ preventScroll: false });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target;
    if (isEditableTarget(target)) return;
    const container = containerRef.current;
    const onRow = target instanceof HTMLElement && target.hasAttribute("data-task-row");
    if (target !== container && !onRow) return;

    const key = event.key;
    if (key === "c") {
      const input = container?.querySelector<HTMLInputElement>('[data-slot="quick-add-input"]');
      if (input) {
        event.preventDefault();
        input.focus();
      }
      return;
    }
    if (key === "ArrowDown" || key === "j") {
      event.preventDefault();
      focusRow(focused + 1);
      return;
    }
    if (key === "ArrowUp" || key === "k") {
      event.preventDefault();
      focusRow(focused - 1);
      return;
    }
    if (key === "Home") {
      event.preventDefault();
      focusRow(0);
      return;
    }
    if (key === "End") {
      event.preventDefault();
      focusRow(flat.length - 1);
      return;
    }
    if (!focusedTask) return;
    switch (key) {
      case "Enter":
        event.preventDefault();
        onOpenTask?.(focusedTask.id);
        return;
      case "x":
      case " ":
        event.preventDefault();
        void services.tasks.toggleComplete(focusedTask.id);
        return;
      case "e":
        event.preventDefault();
        setEditingId(focusedTask.id);
        return;
      case "d":
        event.preventDefault();
        setDueOpenId(focusedTask.id);
        return;
      case "t":
        event.preventDefault();
        setTagsOpenId(focusedTask.id);
        return;
      case "Backspace":
      case "Delete":
        event.preventDefault();
        void services.tasks.delete(focusedTask.id);
        return;
      case "Escape":
        event.preventDefault();
        container?.blur();
        return;
      default:
        break;
    }
    const priority = PRIORITY_KEYS[key];
    if (priority !== undefined) {
      event.preventDefault();
      void services.tasks.setPriority(focusedTask.id, priority);
    }
  };

  const onFocus = () => setActive(true);
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node) || !containerRef.current?.contains(next)) setActive(false);
  };

  const renderGroups = (
    orderedGroups: readonly TaskGroup[],
    dragging = false,
    saving = false,
    canDrop: (group: TaskGroup) => boolean = () => true,
  ) => (
    <div
      ref={containerRef}
      role="listbox"
      aria-label={ariaLabel ?? "Tasks"}
      tabIndex={0}
      data-slot="task-list"
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn("@container flex flex-col gap-1 outline-none focus-ring rounded-md", className)}
    >
      {quickAdd !== undefined && <div className="pb-3">{quickAdd}</div>}
      {allGroups.every((group) => group.tasks.length === 0) && !dragging ? (
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : (
        orderedGroups.map((group) => {
          if (
            group.tasks.length === 0 &&
            !emptyKeys.has(group.key) &&
            (!dragging || !canDrop(group))
          )
            return null;
          const collapsed = collapsedKeys.has(group.key) && !dragging;
          const label = formatGroupLabel ? formatGroupLabel(group) : group.label;
          return (
            <section
              key={group.key}
              data-slot="task-group"
              data-group-key={group.key}
              aria-label={group.key === "evening" ? "Evening plan" : label || undefined}
            >
              <TaskDropGroup group={group} enabled={reorderable} acceptDrop={canDrop(group)}>
                {collapsibleKeys.has(group.key) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-4"
                    aria-expanded={!collapsed}
                    onClick={() => onToggleGroup?.(group.key)}
                  >
                    {label} · {group.tasks.length} {collapsed ? "— Show" : "— Hide"}
                  </Button>
                ) : (
                  label && (
                    <h3
                      data-slot="task-group-heading"
                      className="flex items-baseline gap-2 px-2 pt-4 pb-2 text-xs font-medium text-fg-muted"
                    >
                      {label}
                      <span className="font-normal text-fg-faint">{group.tasks.length}</span>
                    </h3>
                  )
                )}
                {!collapsed && (
                  <OrderedGroup
                    group={group.key}
                    tasks={group.tasks}
                    reorderable={reorderable}
                    saving={saving}
                  >
                    {(task) => {
                      const index = taskIndices.get(task.id) ?? -1;
                      return (
                        <TaskRow
                          key={task.id}
                          task={task}
                          index={index}
                          now={now}
                          projects={projects}
                          focused={active && index === focused}
                          showProject={showProject}
                          density={density}
                          editing={editingId === task.id}
                          onEditingChange={(editing) => {
                            setEditingId(editing ? task.id : undefined);
                            if (!editing) focusRow(index);
                          }}
                          dueDateOpen={dueOpenId === task.id}
                          onDueDateOpenChange={(open) => {
                            setDueOpenId(open ? task.id : undefined);
                            if (!open) focusRow(index);
                          }}
                          tagsOpen={tagsOpenId === task.id}
                          onTagsOpenChange={(open) => {
                            setTagsOpenId(open ? task.id : undefined);
                            if (!open) focusRow(index);
                          }}
                          onSelect={() => focusRow(index)}
                          {...(onOpenTask ? { onOpen: (t: Task) => onOpenTask(t.id) } : {})}
                          {...(onTagClick ? { onTagClick } : {})}
                        />
                      );
                    }}
                  </OrderedGroup>
                )}
                {!collapsed && group.tasks.length === 0 && (
                  <div className="min-h-12 px-9 py-3 text-xs text-fg-muted">
                    {dragging ? "Drop task here" : "No tasks"}
                  </div>
                )}
              </TaskDropGroup>
            </section>
          );
        })
      )}
    </div>
  );
  return reorderable ? (
    <TaskOrder groups={allGroups} onMoveToGroup={onMoveToGroup} canMoveToGroup={canMoveToGroup}>
      {(ordered, state) => renderGroups(ordered, state.dragging, state.saving, state.canDrop)}
    </TaskOrder>
  ) : (
    renderGroups(allGroups)
  );
}

function OrderedGroup({
  tasks,
  group,
  saving,
  reorderable,
  children,
}: {
  tasks: readonly Task[];
  group: string;
  saving: boolean;
  reorderable: boolean;
  children: (task: Task, index: number) => ReactNode;
}) {
  return reorderable ? (
    <div role="group">
      {tasks.map((task, index) => (
        <SortableTask key={task.id} task={task} index={index} group={group} disabled={saving}>
          {children(task, index)}
        </SortableTask>
      ))}
    </div>
  ) : (
    <div role="group" className="flex flex-col pl-7">
      {tasks.map((task, index) => (
        <div key={task.id}>{children(task, index)}</div>
      ))}
    </div>
  );
}
